import { useCreateConversationMessage } from "#features/conversations/hooks/useCreateConversationMessage.ts";
import { settlePendingConnection } from "#features/conversations/lib/connectionRecords.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

// A request resumes once. A read that lands before the run does replays it open, and must not resume it again. A failed
// resume is not retried either: the card stays open, and a typed message supersedes it.
const resumedInterruptIds = new Set<string>();

/** Resumes the run a connection request paused: `resolved` once its integrations are connected, `dismissed` when the user passed on all of them. */
export const usePendingConnection = (conversationId: string) => {
    const { createConversationMessage } = useCreateConversationMessage();
    const queryClient = useQueryClient();

    return useCallback((interruptId: string, outcome: "resolved" | "dismissed") => {
        if (resumedInterruptIds.has(interruptId)) {
            return;
        }
        resumedInterruptIds.add(interruptId);
        // The resume carries no data: core checks what is connected itself.
        createConversationMessage(conversationId, "", {
            resume: [{ interruptId, status: outcome === "resolved" ? "resolved" : "cancelled" }],
            // The resume may have reached core despite the error, so refetch rather than trust the card's state.
            onError: () => queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetConversation(conversationId) })
        });
        settlePendingConnection(queryClient, conversationId, interruptId, outcome);
    }, [conversationId, createConversationMessage, queryClient]);
};
